import React, { useEffect, useMemo, useState } from 'react';
import { Card, Modal } from 'react-bootstrap';
import notistack from 'modules/core/notistack';
import { Controller, useForm } from 'react-hook-form';
import DataGrid, {
    Column,
    Pager,
    Paging,
    Summary,
    TotalItem,
    Scrolling,
    Toolbar,
    Item
} from 'devextreme-react/cjs/data-grid';
import CustomStore from 'devextreme/data/custom_store';
import { GetAllKaratList, getInterStoreInventoryProduct, GetStylesSelectList } from '../services/interStore-transfer.service';
import { useDispatch } from 'react-redux';
import { addProduct, clearProducts } from 'slices/authSlice/interStoreProductSlice';
import { useNavigate } from 'react-router-dom';

interface OptionType {
    selected: boolean;
    text: string;
    value: string;
}

interface FormValues {
    style: string;
    karat: string;
}

const InterStoreProductAccess = () => {
    const [styleOptions, setStyleOptions] = useState<OptionType[]>([]);
    const [ktOptions, setKtOptions] = useState<OptionType[]>([]);
    const [filters, setFilters] = useState<FormValues>({ style: '', karat: '' });
    const [showModal, setShowModal] = useState(false);

    // NEW: size states
    const [sizeOptions, setSizeOptions] = useState<string[]>([]);
    const [selectedSize, setSelectedSize] = useState<string>('');

    const { handleSubmit, control, formState: { errors }, reset } = useForm<FormValues>({
        defaultValues: { style: '', karat: '' }
    });

    const dispatch = useDispatch();
    const navigate = useNavigate();

    // NOTE: include selectedSize so the store is recreated when size changes
    const dataSource = useMemo(() => new CustomStore({
        key: 'id',
        load: async () => {
            try {
                if (!filters.style || !filters.karat) {
                    setSizeOptions([]); // clear sizes when no filters
                    return { data: [], totalCount: 0 };
                }

                const response = await getInterStoreInventoryProduct(filters.style, filters.karat);
                const productData = response?.data || [];

                // Extract unique sizes
                const sizes: string[] = Array.from(
                    new Set(
                        productData.map((item: any) => (item.size ?? '').toString().trim()).filter(Boolean)
                    )
                );
                setSizeOptions(sizes);

                // Apply selected size filter (frontend)
                const filteredData = selectedSize
                    ? productData.filter((p: any) => (p.size ?? '').toString().trim() === selectedSize)
                    : productData;

                return { data: filteredData, totalCount: filteredData.length };
            } catch (error: any) {
                console.error("Data loading error:", error);
                notistack.error("Failed to load products");
                return { data: [], totalCount: 0 };
            }
        }
    }), [filters, selectedSize]);

    const getFilterData = async () => {
        try {
            const [styleRes, KaratRes] = await Promise.all([
                GetStylesSelectList(),
                GetAllKaratList()
            ]);

            if (styleRes?.status === 200 && Array.isArray(styleRes.data)) setStyleOptions(styleRes.data);
            if (KaratRes?.status === 200 && Array.isArray(KaratRes.data)) setKtOptions(KaratRes.data);
        } catch (error: any) {
            notistack.error(error.message);
            console.log(error);
        }
    };

    const onSubmit = (data: FormValues) => {
        setSelectedSize('');
        setFilters({ style: data.style, karat: data.karat });
        setShowModal(false);
        reset({ style: '', karat: '' });
    };

    useEffect(() => {
        dispatch(clearProducts());
    }, [])

    return (
        <>
            <div className="page-content">
                <div className="container-fluid ">
                    <Card>
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <h5 className="m-0">Inter Store Transfer</h5>
                            <button
                                className='btn submit-btn'
                                onClick={async () => {
                                    setShowModal(true);
                                    await getFilterData();
                                }}
                            >
                                Open Filter
                            </button>
                        </Card.Header>

                        <Card.Body className='m-0 p-0'>
                            {/* DataGrid with toolbar size dropdown (replaces search panel) */}
                            <DataGrid
                                dataSource={dataSource}
                                keyExpr="id"
                                columnAutoWidth
                                noDataText={
                                    filters.style && filters.karat
                                        ? "No records found"
                                        : "Please select Style and Karat to search"
                                }
                            >
                                {/* Toolbar with Size dropdown where search panel usually is */}
                                <Toolbar>
                                    <Item location="before">
                                        <div className="d-flex mx-2 align-items-center">
                                            <label className="me-2 mb-0 fw-bold">Size:</label>
                                            <select
                                                className="form-select form-select-sm"
                                                value={selectedSize}
                                                onChange={(e) => setSelectedSize(e.target.value)}
                                                disabled={!sizeOptions.length}
                                            >
                                                <option value="">All Sizes</option>
                                                {sizeOptions.map((s) => (
                                                    <option key={s} value={s}>{s}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </Item>
                                </Toolbar>

                                <Scrolling mode='standard' useNative scrollByContent scrollByThumb />
                                <Paging defaultPageSize={10} />
                                <Pager allowedPageSizes={[10, 25, 50, 100]} showPageSizeSelector showNavigationButtons displayMode="full" showInfo />
                                <Summary><TotalItem /></Summary>

                                <Column
                                    caption="Action"
                                    cssClass="text-center"
                                    cellRender={(cellData) => (
                                        <button
                                            className='btn add-btn'
                                            onClick={() => {
                                                dispatch(addProduct(cellData.data));
                                                navigate('/interStore-payment', { state: { product: cellData.data } });
                                            }}
                                        >
                                            Request
                                        </button>
                                    )}
                                    allowSorting={false}
                                    allowFiltering={false}
                                />
                                <Column dataField="styleNumber" caption="Style Number" />
                                <Column dataField="materialType" caption="Material Type" />
                                <Column dataField="category" caption="Category" />
                                <Column dataField="karatType" caption="Metal Kt" />
                                {/* <Column dataField="ktId" caption="Ktid" defaultVisible={false} allowFiltering={false} /> */}
                                <Column dataField="netWeight" caption="Net Weight" format="#,##0.000" />
                                <Column dataField="size" caption="Size" />
                                <Column dataField="dpPrice" caption="Dp Price" format="#,##0.00" />
                                <Column dataField="customerAmount" caption="MRP" format="#,##0.00" />
                                <Column dataField="huid" caption="HUID" />
                                <Column dataField="stonePrice" caption="Stone Price" format="#,##0.00" />
                                <Column dataField="isSrp" caption="Product Type"
                                    calculateCellValue={(rowData) => (rowData.isSrp ? "SRP" : "MRP")}
                                />
                            </DataGrid>
                        </Card.Body>
                    </Card>
                </div>
            </div>

            {/* Filter Modal */}
            <Modal
                show={showModal}
                onHide={() => {
                    setShowModal(false)
                     reset({ style: '', karat: '' });
                }}
                className="fade"
                dialogClassName="modal-dialog-centered"
                backdrop="static"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Product Details</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <form onSubmit={handleSubmit(onSubmit)} className="container py-3">
                        <div className="row g-3">
                            <div className="col-md-6">
                                <label className="form-label fw-medium">
                                    Style <span className="text-danger">*</span>
                                </label>
                                <Controller
                                    name="style"
                                    control={control}
                                    rules={{ required: "Style no. is required" }}
                                    render={({ field }) => (
                                        <select {...field} className="form-select">
                                            <option value="">Select Style</option>
                                            {styleOptions.map(opt => (
                                                <option key={opt.value} value={opt.text}>
                                                    {opt.text}
                                                </option>
                                            ))}
                                        </select>
                                    )}
                                />
                                {errors.style && (
                                    <div className="text-danger small mt-1">{errors.style.message}</div>
                                )}
                            </div>

                            <div className="col-md-6">
                                <label className="form-label fw-medium">
                                    Karat <span className="text-danger">*</span>
                                </label>
                                <Controller
                                    name="karat"
                                    control={control}
                                    rules={{ required: "Karat is required" }}
                                    render={({ field }) => (
                                        <select {...field} className="form-select">
                                            <option value="">Select Karat</option>
                                            {ktOptions.map(opt => (
                                                <option key={opt.value} value={opt.value}>
                                                    {opt.text}
                                                </option>
                                            ))}
                                        </select>
                                    )}
                                />
                                {errors.karat && (
                                    <div className="text-danger small mt-1">{errors.karat.message}</div>
                                )}
                            </div>
                        </div>
                        <div className="d-flex justify-content-end mt-3">
                            <button type="submit" className="btn submit-btn">
                                Submit
                            </button>
                        </div>
                    </form>
                </Modal.Body>
            </Modal>
        </>
    );
};

export default InterStoreProductAccess;
